import { SlidingTabs } from '@components'
import { useNavigate } from '@integrations/routing'
import { TRANSACTION_SECTIONS } from '@transactions/utils/sections'
import { isOwnerOrAdministrator, paths, selectCurrentUserRole, useAppSelector } from 'mmfintech-backend-api'
import { tr } from 'mmfintech-commons'

interface Props {
  /** The section being shown. */
  active: string
  /**
   * The page that owns the tab bar shows All transactions and Recurring payments itself and passes this.
   * Without it (Recipients, Batch overview) those two tabs open /transactions on that section.
   */
  onSectionChange?: (section: string) => void
}

/** The tab bar under the Transactions title, shared by every page of the Transactions area. */
function TransactionsTabs({ active, onSectionChange }: Props) {
  const navigate = useNavigate()
  const customerRole = useAppSelector(selectCurrentUserRole)

  const tabs = [
    { value: TRANSACTION_SECTIONS.ALL, label: tr('FRONTEND.TRANSACTIONS.SECTIONS.ALL', 'All transactions') },
    {
      value: TRANSACTION_SECTIONS.RECURRING,
      label: (
        <span data-test='button-recurring-payments'>
          {tr('FRONTEND.TRANSACTIONS.RECURRING_PAYMENTS_BUTTON', 'Recurring payments')}
        </span>
      )
    },
    {
      value: TRANSACTION_SECTIONS.RECIPIENTS,
      label: <span data-test='button-recipients'>{tr('FRONTEND.TRANSACTIONS.RECIPIENTS_BUTTON', 'Recipients')}</span>
    },
    {
      value: TRANSACTION_SECTIONS.BATCH,
      label: (
        <span data-test='button-batch-overview'>{tr('FRONTEND.TRANSACTIONS.BATCH_OVERVIEW', 'Batch Overview')}</span>
      ),
      hidden: !isOwnerOrAdministrator(customerRole)
    }
  ]

  function handleChange(value: string) {
    if (value === active) return
    if (value === TRANSACTION_SECTIONS.RECIPIENTS) {
      navigate(paths.recipients.list())
    } else if (value === TRANSACTION_SECTIONS.BATCH) {
      navigate(paths.banking.transactions.batchList())
    } else if (onSectionChange) {
      onSectionChange(value)
    } else {
      navigate(paths.banking.transactions.list(), { state: { section: value } })
    }
  }

  return (
    <SlidingTabs
      tabs={tabs}
      activeTab={active}
      setActiveTab={handleChange}
      memoryKey='transactions-tabs'
      className='transactions-tabs'
    />
  )
}

export default TransactionsTabs
